<template>
  <div class="d-inline-block">
    <m-tooltip
      trigger="hover"
      placement="top"
      :content="name"
      :target="tooltipId"
      :class="wrapperStyle"
    />
    <div
      v-if="initial"
      ref="avatarContent"
      :id="tooltipId"
      :class="avatarStyle"
      :style="Number.isNaN(+size) ? null : avatarSizeWithNumber"
    >
      <div
        class="avatar-initial"
        :style="Number.isNaN(+size) ? null : avatarInitialSizeWithNumber"
      >
        {{ name | initials(filterInitial) }}
      </div>
    </div>
    <template v-else>
      <div
        v-if="hasImage"
        ref="avatarContent"
        :id="tooltipId"
        :class="avatarStyle"
        :style="Number.isNaN(+size) ? null : avatarSizeWithNumber"
      >
        <img
          class="avatar-photo"
          :src="src"
          :alt="name"
          :title="name"
        >
      </div>
      <div
        v-else
        ref="avatarContent"
        :id="tooltipId"
        :class="avatarStyle"
        :style="Number.isNaN(+size) ? null : avatarSizeWithNumber"
      />
    </template>
  </div>
</template>

<script>
// This component's style is handled by http://mekari-ui.sleekr.id/docs/1.0/components/avatar
// TODO: Implement tooltip support
import MTooltip from '../Tooltip';

export default {
  name: 'Avatar',
  components: {
    MTooltip,
  },
  filters: {
    initials(value, filter) {
      const defaultNameLength = 2;
      if (!filter) {
        return value;
      }

      let names = value.split(' ');
      names = names.filter(name => name !== '');

      if (names.length >= defaultNameLength) {
        return names.filter((x, y) => y === 0 || y === names.length - 1)
          .map(n => n[0])
          .join('')
          .toUpperCase();
      } if (names.length === 1 && value !== '') {
        return `${value.charAt(0).toUpperCase()}`;
      }

      return '';
    },
  },
  props: {
    /**
     * Avatar's size (width x height). The sizes are:
     *
     * **xsmall**: 24px
     *
     * **small**: 32px
     *
     * **medium**: 40px
     *
     * **large**: 60px
     *
     * **xlarge**: 80px
     *
     * **xxlarge**: 120px
     */
    size: {
      type: [String, Number],
      default: 'medium',
      validator: value =>
        Number.isNaN(+value)
          ? ['xsmall', 'small', 'medium', 'large', 'xlarge', 'xxlarge'].indexOf(value) !== -1
          : +value > 0
      ,
    },
    /**
     * Avatar's image source
     */
    src: {
      type: String,
      default: '',
    },
    /**
     * Avatar's name, will be used as alt, title, and initial if initial is enabled
     */
    name: {
      type: String,
      default: 'Avatar',
    },
    /**
     * If enabled, the avatar will be displayed as an initial, even you pass a valid image source.
     * If disabled, it will use the given image source.
     */
    initial: {
      type: Boolean,
      default: false,
    },
    /**
     * If enabled, the avatar's name will be filtered, eg. `John Doe` will become `JD`. Meanwhile `john` will be `Jo`.
     * If disabled, it will display the name.
     */
    filterInitial: {
      type: Boolean,
      default: true,
    },
    /**
     * Display circle/rounded avatar
     */
    circle: {
      type: Boolean,
      default: true,
    },
    /**
     * Avatar activator id for tooltip
     */
    tooltipId: {
      type: String,
      default: null,
    },
    /**
     * If set to `true`, when avatar hovered, it will have a black overlay. If set to `false`, the black overlay will not shown no matter what action happen on the avatar.
     */
    showOverlay: {
      type: Boolean,
      default: true,
    },
    /**
     * If set to `true`, when avatar hovered, it will show the photo icon. If set to `false`, it will never show the photo icon when hovered.
     */
    showOverlayIcon: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    hasImage() {
      return this.src && this.src !== '';
    },
    defaultStyle() {
      return {
        'avatar-xsmall': this.size === 'xsmall',
        'avatar-small': this.size === 'small',
        'avatar-medium': this.size === 'medium',
        'avatar-large': this.size === 'large',
        'avatar-xlarge': this.size === 'xlarge',
        'avatar-xxlarge': this.size === 'xxlarge',
        'avatar-default-people': !this.initial && !this.hasImage,
        'avatar--no-overlay': !this.showOverlay,
        'avatar--overlay-icon': this.showOverlay && this.showOverlayIcon,
      };
    },
    wrapperStyle() {
      return {
        'rounded-circle': this.circle,
        ...this.defaultStyle,
      };
    },
    avatarSizeWithNumber() {
      return {
        width: `${this.size}px`,
        height: `${this.size}px`,
      };
    },
    avatarInitialSizeWithNumber() {
      return {
        color: '#777',
        width: `${this.size}px`,
        height: `${this.size}px`,
        'line-height': `${this.size}px`,
        'font-size': `calc(${this.size}px * .375)`,
      };
    },
    avatarStyle() {
      return {
        avatar: true,
        'avatar-circle': this.circle,
        ...this.defaultStyle,
      };
    },
  },
};
</script>
